import { css } from '@styled-system/css';
import { Layers, Play } from 'lucide-react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useNicheSoldProducts } from '@/features/analysisPageNiches/hooks/useNicheSoldProducts';
import type { AnalysisNiche } from '@/features/analysisPageNiches/types';
import { getFormattedNicheName } from '@/features/analysisPageNiches/utils/formatNicheName';
import { VestiaireLockedButton } from '@/shared/components/analysis/VestiaireLockedChip';
import { ProductFeed } from '@/shared/components/product/ProductFeed';
import { Button } from '@/shared/components/ui/Button';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Message } from '@/shared/components/ui/Message';

// ============================================================================
// Drill-down des ventes d'une niche — drawer DS large (~70vw) : les cartes
// feed s'affichent en layout wide et le tableau reste visible sur le bord.
// ============================================================================

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '5',
  height: '100%',
  minHeight: 0,
  bg: 'surface.default',
  paddingTop: '6',
  paddingBottom: '8',
  paddingX: '10',
});

const header = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  paddingRight: '56px', // laisse la place au bouton de fermeture du Drawer
  flexShrink: 0,
});

const nicheImage = css({
  width: '48px',
  height: '48px',
  borderRadius: 'lg',
  objectFit: 'cover',
  bg: 'surface.sunken',
  flexShrink: 0,
});

const nicheImageFallback = css({
  width: '48px',
  height: '48px',
  borderRadius: 'lg',
  bg: 'surface.sunken',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

const titleStack = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5',
  minWidth: 0,
  flex: 1,
});

const nichePath = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const nicheTitle = css({
  fontSize: 'h3',
  lineHeight: 'h3',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.3px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const subtitle = css({
  fontSize: 'caption',
  color: 'text.tertiary',
});

const feedWrap = css({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '3',
  paddingRight: '2',
});

export interface NicheSellsHistModalProps {
  analysisId: string;
  brandOrFullPath: string;
  nicheImageUrl?: string;
  niche: string;
  allNiches: AnalysisNiche[];
  onClose: () => void;
  /** Lance une recherche ciblée sur cette niche (bouton du header). */
  onLaunchSearch?: () => void;
  /** Relance verrouillée par le plan (cf `NichesTable.launchLocked`) : grisée, cadenas, infobulle. */
  launchLocked?: boolean;
}

export const NicheSellsHistModal: FC<NicheSellsHistModalProps> = ({
  analysisId,
  brandOrFullPath,
  nicheImageUrl,
  niche,
  allNiches,
  onClose,
  onLaunchSearch,
  launchLocked = false,
}) => {
  const { t } = useTranslation();
  const formattedNiche = getFormattedNicheName(niche, allNiches);

  const {
    products: allProducts,
    isLoading: productsLoading,
    error: productsError,
  } = useNicheSoldProducts({
    analysisId: analysisId ?? '',
    brandOrFullPath,
    enabled: !!analysisId && !!brandOrFullPath,
  });

  return (
    <Drawer isOpen onClose={onClose} width="max(900px, 70vw)">
      <div className={container}>
        {/* ─── Header : image + nom + CTA ────────────────────────────── */}
        <div className={header}>
          {nicheImageUrl ? (
            <img src={nicheImageUrl} alt="" className={nicheImage} />
          ) : (
            <span className={nicheImageFallback}>
              <Layers size={18} strokeWidth={1.75} />
            </span>
          )}
          <div className={titleStack}>
            {formattedNiche.path && formattedNiche.hasDuplicate && (
              <span className={nichePath}>{formattedNiche.path}</span>
            )}
            <span className={nicheTitle}>{formattedNiche.name}</span>
            <span className={subtitle}>{t('niches.drawerSubtitle')}</span>
          </div>
          {onLaunchSearch &&
            (launchLocked ? (
              <VestiaireLockedButton variant="secondary" size="md" iconSize={13}>
                {t('salesFeed.similar')}
              </VestiaireLockedButton>
            ) : (
              <Button
                variant="secondary"
                size="md"
                icon={<Play size={13} strokeWidth={1.75} />}
                onClick={onLaunchSearch}
              >
                {t('salesFeed.similar')}
              </Button>
            ))}
        </div>

        {/* ─── Feed des ventes ───────────────────────────────────────── */}
        <div className={feedWrap}>
          {productsError ? (
            <Message type="error" bordered>
              {productsError}
            </Message>
          ) : (
            <ProductFeed staticProducts={allProducts} status="sold" isLoading={productsLoading} />
          )}
        </div>
      </div>
    </Drawer>
  );
};
